import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { Box, Skeleton } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import NonIdealNew from "../../../../common/NonIdealNew";
import { ParagraphText } from "../../../../../Libraries";
import ReportPageHeader from "../ReportPageHeader";
import { FilterNonIdeal } from "../../../../common";
import pageStyles from "../styles.module.css";
import { LanguageContext } from "../../../../../context/LanguageContext";
import { useReports } from "../../../../../hooks/useReports";
import { useAuthContext } from "../../../../../context/AuthContext";
import {
  formatReportCurrency,
  getStartAndEndDate,
  REPORT_DATE_FILTERS,
  REPORT_PAGINATION_LIMIT,
  REPORT_SKELETON_ROWS,
} from "../../../../../utils";
import AppPagination from "../../../../../Libraries/AppPagination";
import dayjs from "dayjs";
import useReportExport from "../../../../../hooks/useReportExport";
import useReportFilter from "../../../../../hooks/useReportFilter";
import { analytics, triggerLogEvent } from "../../../../../config/firebase";

const ExpenseByCategory = () => {
  const [I18n] = useContext(LanguageContext);
  const { handleFetchReport, loading, pageLoading, expenseByCategory, expenseByCategoryTotal } = useReports(I18n);
  const { business } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const currentDates = useRef(null);

  const fetchReport = useCallback((start, end, offset = 0) => {
    handleFetchReport({
      business_id: business?.id,
      report_type: "expense_by_category",
      start_date: dayjs(start?.toDate ? start.toDate() : start).toISOString(),
      end_date: dayjs(end?.toDate ? end.toDate() : end).toISOString(),
      limit: REPORT_PAGINATION_LIMIT,
      offset,
    });
  }, [handleFetchReport, business?.id]);


  useEffect(() => {
    triggerLogEvent(analytics, "report_expense_by_category_visited", {});
  }, []);

  const { handleExport, exportLoading } = useReportExport("expense_by_category");

  const onFetch = useCallback((start, end) => {
    currentDates.current = { start, end };
    fetchReport(start, end, 0);
  }, [fetchReport]);

  const { headerDefaultFilter, isYearlyFilter, resetCount, handleFilterDate, handleResetFilter } = useReportFilter(onFetch, "expense_by_category");

  const handleNext = useCallback((offset) => {
    if (!currentDates.current) return;
    fetchReport(currentDates.current.start, currentDates.current.end, offset);
  }, [fetchReport]);

  const showNonIdeal = isYearlyFilter && !loading && expenseByCategory.length === 0;

  const minDate = useMemo(() => dayjs().subtract(1, "year"), []);
  const maxDate = useMemo(() => dayjs().endOf("day"), []);

  return (
    <Box id="expense_by_category_page_wrapper" className={pageStyles.pageWrapper}>
      <ReportPageHeader
        title={I18n.expense_by_category}
        id="expense_by_category"
        dateFilters={REPORT_DATE_FILTERS}
        onFilterDate={handleFilterDate}
        minDate={minDate}
        maxDate={maxDate}
        defaultFilter={headerDefaultFilter}
        resetKey={resetCount}
        hideActions={showNonIdeal}
        onExport={handleExport}
        exportLoading={exportLoading}
      />

      {showNonIdeal ? (
        <Box id="expense_by_category_nonideal_body" className={pageStyles.pageBody}>
          <NonIdealNew
            buttonOneId="expense_by_category_nonideal_start_expense_btn"
            title={I18n.no_data_yet}
            subtitle={I18n.it_looks_like_you_dont_have_any_data_yet}
            button1Title={I18n.add_quick_expense}
            onButton1Click={() => {
              triggerLogEvent(analytics, "report_nonideal_start_new_expense", { report_type: "expense_by_category" });
              navigate("/Expense/ExpenseForm", { state: { pathname: location.pathname } });
            }}
            loading={false}
            disabledOne={false}
          />
        </Box>
      ) : (
        <>
          {(loading || expenseByCategory.length > 0) && (
            <Box id="expense_by_category_table_header" className="invoiceListTableHeader">
              <Box id="expense_by_category_header_category" className="invListTableHeaderCol fourColumn textLeft">
                <ParagraphText id="expense_by_category_header_label_category" className="fontSize12 fontWeight500 newLightColor">{I18n.category}</ParagraphText>
              </Box>
              <Box id="expense_by_category_header_transactions" className="invListTableHeaderCol fourColumn textLeft">
                <ParagraphText id="expense_by_category_header_label_transactions" className="fontSize12 fontWeight500 newLightColor">{I18n.transactions}</ParagraphText>
              </Box>
              <Box id="expense_by_category_header_total_expense" className="invListTableHeaderCol fourColumn textRight">
                <ParagraphText id="expense_by_category_header_label_total_expense" className="fontSize12 fontWeight500 newLightColor">{I18n.total_expense}</ParagraphText>
              </Box>
              <Box id="expense_by_category_header_contribution" className="invListTableHeaderCol fourColumn textRight">
                <ParagraphText id="expense_by_category_header_label_contribution" className="fontSize12 fontWeight500 newLightColor">{I18n.contribution}</ParagraphText>
              </Box>
            </Box>
          )}
          <Box id="expenseByCategoryScrollable" className={pageStyles.pageBody}>
            {loading ? (
              Array.from({ length: REPORT_SKELETON_ROWS }).map((_, i) => (
                <Box key={i} className="invoiceListTableBody defaultCursorImportant">
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <Skeleton variant="rounded" animation="wave" width={130} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <Skeleton variant="rounded" animation="wave" width={36} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Skeleton variant="rounded" animation="wave" width={80} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                <Box className="invListTableHeaderCol fourColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Skeleton variant="rounded" animation="wave" width={44} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                </Box>
              ))
            ) : expenseByCategory.length === 0 ? (
            <FilterNonIdeal
                title={I18n.no_transactions_matching_those_filters}
                subTitle={`${I18n.try_adjusting_filters} `}
                subTitleText={I18n.view_yearly_data}
                subTitleTextOnClick={handleResetFilter}
                subTitleTextId="expense_by_category_filter_view_yearly"
              />
            ) : (
              <AppPagination
                data={{
                  handleNext,
                  pageLoad: pageLoading,
                  count: expenseByCategoryTotal,
                  list: expenseByCategory,
                  scrollableTarget: "expenseByCategoryScrollable",
                }}
              >
                {expenseByCategory.map(({ category, transactions, total_expense, contribution }, i) => (
                  <Box key={i} id={`expense_by_category_row_${i}`} className="invoiceListTableBody defaultCursorImportant">
                    <Box id={`expense_by_category_row_${i}_category`} className="invListTableHeaderCol fourColumn textLeft">
                      <ParagraphText id={`expense_by_category_cell_${i}_category`} className="fontSize14 newFontColor">{category}</ParagraphText>
                    </Box>
                    <Box id={`expense_by_category_row_${i}_transactions`} className="invListTableHeaderCol fourColumn textLeft">
                      <ParagraphText id={`expense_by_category_cell_${i}_transactions`} className="fontSize14 newFontColor">{transactions}</ParagraphText>
                    </Box>
                    <Box id={`expense_by_category_row_${i}_total_expense`} className="invListTableHeaderCol fourColumn textRight">
                      <ParagraphText id={`expense_by_category_cell_${i}_total_expense`} className="fontSize14 newFontColor">{formatReportCurrency(total_expense)}</ParagraphText>
                    </Box>
                    <Box id={`expense_by_category_row_${i}_contribution`} className="invListTableHeaderCol fourColumn textRight">
                      <ParagraphText id={`expense_by_category_cell_${i}_contribution`} className="fontSize14 newFontColor">
                        {contribution != null ? `${contribution}%` : "—"}
                      </ParagraphText>
                    </Box>
                  </Box>
                ))}
              </AppPagination>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ExpenseByCategory;
